<template>
    <div ref="printContainer" class="print-container p-6">
        <div class="mb-8 flex items-start justify-between gap-4 border-b border-slate-200 pb-5">
            <div>
                <p class="text-xs font-semibold tracking-wide text-indigo-600 uppercase">Interior Calculator</p>
                <h1 class="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Project estimate</h1>
                <p class="mt-1 text-sm text-slate-500">Wall panels, ceiling, and flooring quantities (includes 1% waste)</p>
            </div>
            <button
                type="button"
                class="no-print inline-flex cursor-pointer items-center gap-2 rounded-xl bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500"
                @click="printReport"
            >
                Print
            </button>
        </div>

        <div v-if="!rooms.length" class="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-500">
            No rooms to print. Add rooms and measurements first.
        </div>

        <div v-for="room in rooms" :key="room.id" class="mb-8 break-inside-avoid">
            <h2 class="mb-4 text-lg font-semibold text-slate-900">{{ room.name }}</h2>

            <!-- Panels Section -->
            <div class="mb-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 class="font-semibold text-slate-800">Wall panels</h3>
                    <span class="rounded-lg bg-white px-2.5 py-1 text-sm font-semibold text-indigo-700 tabular-nums ring-1 ring-indigo-100">
                        {{ room.panels.panelCount || 0 }} required
                    </span>
                </div>
                <div class="mb-3 text-sm text-slate-600" v-if="room.panels.product?.name">
                    Product: <span class="font-medium text-slate-800">{{ room.panels.product.name }}</span>
                    <span class="text-slate-400"> · {{ formatProductSize(room.panels.product.width, room.panels.product.length) }}</span>
                </div>
                <div v-else class="mb-3 text-sm text-slate-400">No panel product selected</div>
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <h4 class="text-xs font-semibold tracking-wide text-slate-500 uppercase">Walls</h4>
                        <ul v-if="room.panels.walls.length" class="mt-1 space-y-0.5 text-sm text-slate-700">
                            <li v-for="(wall, index) in room.panels.walls" :key="`wall-${index}`">
                                Wall {{ index + 1 }}: {{ formatMeasurement(wall.width) }} × {{ formatMeasurement(wall.height) }}
                            </li>
                        </ul>
                        <p v-else class="mt-1 text-sm text-slate-400">None</p>
                    </div>
                    <div>
                        <h4 class="text-xs font-semibold tracking-wide text-slate-500 uppercase">Openings</h4>
                        <div v-if="room.panels.windows.length" class="mt-1 text-sm text-slate-700">
                            Windows
                            <ul class="mt-0.5 space-y-0.5">
                                <li v-for="(window, index) in room.panels.windows" :key="`window-${index}`">
                                    {{ formatMeasurement(window.width) }} × {{ formatMeasurement(window.height) }}
                                </li>
                            </ul>
                        </div>
                        <div v-if="room.panels.doors.length" class="mt-1 text-sm text-slate-700">
                            Doors
                            <ul class="mt-0.5 space-y-0.5">
                                <li v-for="(door, index) in room.panels.doors" :key="`door-${index}`">
                                    {{ formatMeasurement(door.width) }} × {{ formatMeasurement(door.height) }}
                                </li>
                            </ul>
                        </div>
                        <div v-if="room.panels.vents.length" class="mt-1 text-sm text-slate-700">
                            Vents
                            <ul class="mt-0.5 space-y-0.5">
                                <li v-for="(vent, index) in room.panels.vents" :key="`vent-${index}`">
                                    {{ formatMeasurement(vent.width) }} × {{ formatMeasurement(vent.height) }}
                                </li>
                            </ul>
                        </div>
                        <p
                            v-if="!room.panels.windows.length && !room.panels.doors.length && !room.panels.vents.length"
                            class="mt-1 text-sm text-slate-400"
                        >
                            None
                        </p>
                    </div>
                </div>
            </div>

            <!-- Ceiling Section -->
            <div class="mb-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 class="font-semibold text-slate-800">Ceiling</h3>
                    <span class="rounded-lg bg-white px-2.5 py-1 text-sm font-semibold text-indigo-700 tabular-nums ring-1 ring-indigo-100">
                        {{ room.ceiling.ceilingCount || 0 }} required
                    </span>
                </div>
                <div class="mb-3 text-sm text-slate-600" v-if="room.ceiling.product?.name">
                    Product: <span class="font-medium text-slate-800">{{ room.ceiling.product.name }}</span>
                    <span class="text-slate-400"> · {{ formatProductSize(room.ceiling.product.width, room.ceiling.product.length) }}</span>
                </div>
                <div v-else class="mb-3 text-sm text-slate-400">No ceiling product selected</div>
                <h4 class="text-xs font-semibold tracking-wide text-slate-500 uppercase">Sections</h4>
                <ul v-if="room.ceiling.roof.length" class="mt-1 space-y-0.5 text-sm text-slate-700">
                    <li v-for="(roof, index) in room.ceiling.roof" :key="`roof-${index}`">
                        Section {{ index + 1 }}: {{ formatMeasurement(roof.length) }} × {{ formatMeasurement(roof.width) }}
                    </li>
                </ul>
                <p v-else class="mt-1 text-sm text-slate-400">None</p>
            </div>

            <!-- Flooring Section -->
            <div class="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 class="font-semibold text-slate-800">Flooring</h3>
                    <span class="rounded-lg bg-white px-2.5 py-1 text-sm font-semibold text-indigo-700 tabular-nums ring-1 ring-indigo-100">
                        {{ room.flooring?.flooringCount || 0 }} required
                    </span>
                </div>
                <div class="mb-3 text-sm text-slate-600" v-if="room.flooring?.product?.name">
                    Product: <span class="font-medium text-slate-800">{{ room.flooring.product.name }}</span>
                    <span class="text-slate-400"> · {{ formatProductSize(room.flooring.product.width, room.flooring.product.length) }}</span>
                </div>
                <div v-else class="mb-3 text-sm text-slate-400">No flooring product selected</div>
                <h4 class="text-xs font-semibold tracking-wide text-slate-500 uppercase">Sections</h4>
                <ul v-if="room.flooring?.floor?.length" class="mt-1 space-y-0.5 text-sm text-slate-700">
                    <li v-for="(floor, index) in room.flooring.floor" :key="`floor-${index}`">
                        Section {{ index + 1 }}: {{ formatMeasurement(floor.length) }} × {{ formatMeasurement(floor.width) }}
                    </li>
                </ul>
                <p v-else class="mt-1 text-sm text-slate-400">None</p>
            </div>
        </div>

        <!-- Project totals (matches calculator summary) -->
        <div
            v-if="rooms.length"
            class="print-project-totals mt-8 break-inside-avoid rounded-2xl bg-gradient-to-br from-indigo-50 to-slate-50 p-4 ring-1 ring-indigo-100/80 dark:from-indigo-950/50 dark:to-slate-900/80 dark:ring-indigo-500/25"
        >
            <div class="flex items-center justify-between gap-2">
                <h2 class="text-xs font-semibold tracking-wide text-indigo-500/80 uppercase dark:text-indigo-300">Project totals</h2>
                <span class="text-[11px] text-slate-500">Includes 1% waste</span>
            </div>
            <div class="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div class="print-total-card rounded-xl bg-white/70 px-3 py-2.5 ring-1 ring-slate-200/80 dark:bg-slate-900/70 dark:ring-white/10">
                    <p class="text-[11px] text-slate-500">Rooms</p>
                    <p class="print-total-value mt-0.5 text-lg font-semibold text-slate-900 tabular-nums dark:text-white">{{ rooms.length }}</p>
                </div>
                <div class="print-total-card rounded-xl bg-white/70 px-3 py-2.5 ring-1 ring-slate-200/80 dark:bg-slate-900/70 dark:ring-white/10">
                    <p class="text-[11px] text-slate-500">Panels</p>
                    <p class="print-total-value mt-0.5 text-lg font-semibold text-slate-900 tabular-nums dark:text-white">{{ totalPanels }}</p>
                </div>
                <div class="print-total-card rounded-xl bg-white/70 px-3 py-2.5 ring-1 ring-slate-200/80 dark:bg-slate-900/70 dark:ring-white/10">
                    <p class="text-[11px] text-slate-500">Ceiling</p>
                    <p class="print-total-value mt-0.5 text-lg font-semibold text-slate-900 tabular-nums dark:text-white">{{ totalCeilings }}</p>
                </div>
                <div class="print-total-card rounded-xl bg-white/70 px-3 py-2.5 ring-1 ring-slate-200/80 dark:bg-slate-900/70 dark:ring-white/10">
                    <p class="text-[11px] text-slate-500">Flooring</p>
                    <p class="print-total-value mt-0.5 text-lg font-semibold text-slate-900 tabular-nums dark:text-white">{{ totalFloorings }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { useAppHelpers } from '@/composables/useAppHelpers.js';
import { printElement } from '@/utils/qzPrint.js';

defineProps({
    rooms: {
        type: Array,
        default: () => [],
    },
    totalPanels: {
        type: Number,
        default: 0,
    },
    totalCeilings: {
        type: Number,
        default: 0,
    },
    totalFloorings: {
        type: Number,
        default: 0,
    },
});

const { formatMeasurement } = useAppHelpers();

/** Product length/width are stored in inches. */
const formatProductSize = (width, length) => {
    const w = Number(width) || 0;
    const l = Number(length) || 0;
    return `${w}" × ${l}"`;
};

const printContainer = ref(null);

// The estimate is the rendered page rather than a generated document, so it goes
// out through printElement — but by the same route as every other print: QZ when
// it is running, browser dialog otherwise. Printing the window instead, as this
// once did, put the whole app shell on the paper.
const printReport = () => printElement(printContainer.value, { size: 'A4' });
</script>

<style>
@media print {
    body {
        margin: 1cm;
    }
    .print-container {
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
    }
    .no-print {
        display: none !important;
    }
    .break-inside-avoid {
        break-inside: avoid;
    }
    /* Keep project totals light on paper even when the app is in dark mode */
    .print-project-totals {
        background-image: linear-gradient(to bottom right, #eef2ff, #f8fafc) !important;
        --tw-ring-color: rgb(224 231 255 / 0.8) !important;
        color: #0f172a !important;
    }
    .print-project-totals h2 {
        color: rgb(99 102 241 / 0.8) !important;
    }
    .print-project-totals .print-total-card {
        background-color: rgb(255 255 255 / 0.7) !important;
        --tw-ring-color: rgb(226 232 240 / 0.8) !important;
    }
    .print-project-totals .print-total-value {
        color: #0f172a !important;
    }
}
</style>
